'use es6';

import { useEffect, Fragment } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import NavMarker from 'react-rhumb/NavMarker';
import { parse as parseParams } from 'hub-http/helpers/params';
import I18n from 'I18n';
import UIEmbeddedComponent from 'ui-addon-iframeable/embed/UIEmbeddedComponent';
import DocumentHead from 'ecosystem-marketplace-core/components/portals/DocumentHead';
import PrerenderReady from 'ecosystem-marketplace-core/components/prerender/PrerenderReady';
import { getIsCustomer, getIsUngatedForAnalyticsLanguage, PublicOnly } from '../../selectors/auth';
import { useScrollToTop } from 'ecosystem-marketplace-core/hooks/useScrollToTop';
import { isSucceeded, isUninitialized } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import { getDataFetchingClientRequestState } from 'marketplace-ui-common/dataFetchingClient/utils/getDataFetchingClientRequestState';
import NotFoundError from '../../components/error/NotFoundError';
import GenericError from '../../components/error/GenericError';
import NoAccessError from '../../components/error/NoAccessError';
import DetailPage from '../../components/detail/DetailPage';
import HSPageViewTracker from '../../components/tracking/HSPageViewTracker';
import { useListingView } from '../../utils/hooks/useListingView';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DetailError = ({
  children,
  statusCode
}) => /*#__PURE__*/_jsxs(Fragment, {
  children: [children, /*#__PURE__*/_jsx(PublicOnly, {
    children: /*#__PURE__*/_jsx(DocumentHead, {
      children: /*#__PURE__*/_jsx(PrerenderReady, {
        statusCode: statusCode
      })
    })
  })]
});
const DetailContainer = props => {
  const {
    children,
    isAdminPreview,
    location: {
      pathname,
      search
    },
    match: {
      path
    },
    onReady
  } = props;
  const isCustomer = useSelector(getIsCustomer);
  useScrollToTop(pathname);
  const isUngatedForAnalyticsLanguage = useSelector(getIsUngatedForAnalyticsLanguage);
  const {
    loading,
    error: listingViewFetchError
  } = useListingView();
  const listingViewFetchStatus = getDataFetchingClientRequestState(loading, listingViewFetchError);
  const {
    published
  } = parseParams(search.slice(1));
  const isPreview = published === 'false' || path === '/_preview/:slug' || isAdminPreview;
  useEffect(() => {
    if (isUninitialized(listingViewFetchStatus)) {
      document.title = I18n.text('appMarketplace.htmlTitle');
    }
  }, [listingViewFetchStatus]);
  useEffect(() => {
    if (isSucceeded(listingViewFetchStatus) && onReady) {
      onReady({
        receiveMessage: () => {}
      });
    }
  }, [listingViewFetchStatus, onReady]);
  if (listingViewFetchError) {
    if (listingViewFetchError.status === 404) {
      return /*#__PURE__*/_jsx(DetailError, {
        statusCode: 404,
        children: /*#__PURE__*/_jsx(NavMarker, {
          name: "DETAIL_NOT_FOUND_ERROR_LOAD",
          children: /*#__PURE__*/_jsx(NotFoundError, {})
        })
      });
    } else if (listingViewFetchError.status === 403) {
      return /*#__PURE__*/_jsx(DetailError, {
        statusCode: 403,
        children: /*#__PURE__*/_jsx(NavMarker, {
          name: "DETAIL_NO_ACCESS_ERROR_LOAD",
          children: /*#__PURE__*/_jsx(NoAccessError, {
            use: isPreview ? 'preview' : 'default'
          })
        })
      });
    } else {
      return /*#__PURE__*/_jsx(DetailError, {
        statusCode: listingViewFetchError.status || 400,
        children: /*#__PURE__*/_jsx(NavMarker, {
          name: "DETAIL_ERROR_LOAD",
          children: /*#__PURE__*/_jsx(GenericError, {})
        })
      });
    }
  }
  if (!isSucceeded(listingViewFetchStatus)) {
    return null;
  }
  const wrappedChildren = children || /*#__PURE__*/_jsx(DetailPage, Object.assign({}, props));
  if (isAdminPreview) {
    return wrappedChildren;
  }
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(HSPageViewTracker, {
      addLang: isCustomer && isUngatedForAnalyticsLanguage,
      overrideTrack: isCustomer && isUngatedForAnalyticsLanguage,
      pathname: pathname
    }), wrappedChildren]
  });
};
DetailContainer.propTypes = {
  children: PropTypes.node,
  isAdminPreview: PropTypes.bool,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.object.isRequired,
    path: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired
  }).isRequired,
  onReady: PropTypes.func
};
DetailContainer.defaultProps = {
  isAdminPreview: false
};
export default UIEmbeddedComponent(DetailContainer, {
  enableTopLevelEmbed: true
});